import { useEffect, useRef, useState } from "react";
import { messageErreur } from "../api";

/**
 * Confirmation d'une action, DANS l'application.
 *
 * Elle remplace `window.confirm`, et ce n'est pas une question de style.
 * Une boîte du navigateur BLOQUE l'onglet entier tant que personne n'a
 * cliqué : plus une ligne de code ne s'exécute, plus rien ne répond. C'est
 * ce qui a fait conclure à un audit du 21/08/2026 que « Marquer comme
 * effectuée » gelait l'application 45 secondes à 3 minutes — son navigateur
 * piloté ne cliquait pas, et attendait jusqu'à expiration. Le logiciel, lui,
 * répondait en quelques dizaines de millisecondes.
 *
 * Trois autres raisons, valables pour un vrai utilisateur :
 *   - une boîte système ne porte NI la marque du client ni ses couleurs ;
 *   - sur téléphone elle s'affiche hors contexte, en haut de l'écran ;
 *   - elle ne sait rien faire d'autre qu'accepter ou refuser : impossible
 *     d'attendre le serveur, d'afficher un échec ou de garder la main.
 *
 * D'où le contrat de ce composant : `onConfirmer` peut être une promesse.
 * Pendant qu'elle court, LES DEUX BOUTONS SONT DÉSACTIVÉS — sans quoi un
 * second clic renvoie l'action, et la deuxième réponse (un refus, puisque
 * la première a réussi) s'affiche comme un échec sur une action qui a marché.
 * La modale ne se ferme qu'en cas de SUCCÈS : un échec reste affiché ici,
 * là où l'utilisateur regarde.
 */
export default function ModaleConfirmation({
  titre,
  sousTitre,
  message,
  enfants,
  libelleConfirmer = "Confirmer",
  libelleAnnuler = "Annuler",
  danger = false,
  confirmerDesactive = false,
  onConfirmer,
  onFermer,
}) {
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const boutonRef = useRef(null);

  // Le focus arrive sur le bouton de confirmation : au clavier, la modale est
  // utilisable sans chercher où l'on se trouve.
  useEffect(() => {
    boutonRef.current?.focus();
  }, []);

  // Échap referme, comme partout ailleurs — mais jamais pendant l'envoi :
  // fermer l'écran pendant qu'une écriture est en vol laisserait l'utilisateur
  // sans nouvelle de son sort.
  useEffect(() => {
    const surTouche = (e) => {
      if (e.key === "Escape" && !envoi) onFermer();
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [envoi, onFermer]);

  const confirmer = async () => {
    setErreur("");
    setEnvoi(true);
    try {
      await onConfirmer();
      onFermer();
    } catch (err) {
      setErreur(messageErreur(err, "Action impossible."));
      setEnvoi(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !envoi && onFermer()}>
      <div
        className="modal-carte"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h3 className="modal-titre">{titre}</h3>
        {sousTitre && <p className="modal-sous-titre">{sousTitre}</p>}
        {message && <p className="modal-message">{message}</p>}
        {enfants}
        {erreur && <p className="modal-erreur">{erreur}</p>}
        <div className="modal-actions">
          <button className="btn" onClick={onFermer} disabled={envoi}>
            {libelleAnnuler}
          </button>
          <button
            ref={boutonRef}
            className={danger ? "btn-danger" : "btn-accent"}
            onClick={confirmer}
            disabled={envoi || confirmerDesactive}
          >
            {envoi ? "…" : libelleConfirmer}
          </button>
        </div>
      </div>
    </div>
  );
}
